import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { UserCircleIcon } from '@heroicons/react/24/outline';

const ProfileForm = () => {
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not found');

      // Récupérer les informations depuis user_settings au lieu de profiles
      const { data: userSettings, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      // Si pas de user_settings, créer un enregistrement par défaut
      if (error && error.code === 'PGRST116') {
        const { data: newSettings, error: createError } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            full_name: user.user_metadata?.full_name || '',
            notification_settings: {
              enabled: false,
              sound: true,
              newMessage: true,
              mentions: true,
              status: true
            }
          })
          .select()
          .single();

        if (createError) throw createError;
        
        setProfile({
          id: user.id,
          full_name: newSettings.full_name || '',
          email: user.email,
          new_password: '',
          confirm_password: '',
        });
      } else if (error) {
        throw error;
      } else {
        setProfile({
          id: user.id,
          full_name: userSettings.full_name || user.user_metadata?.full_name || '',
          email: user.email,
          new_password: '',
          confirm_password: '',
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePasswords = () => {
    if (profile.new_password && profile.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (profile.new_password !== profile.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (profile.new_password && !validatePasswords()) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Update profile dans user_settings
      const { error: profileError } = await supabase
        .from('user_settings')
        .update({
          full_name: profile.full_name,
          updated_at: new Date(),
        })
        .eq('user_id', profile.id);

      if (profileError) throw profileError;

      // Update password if provided
      if (profile.new_password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: profile.new_password,
        });

        if (passwordError) throw passwordError;
      }

      setSuccess('Profil mis à jour avec succès');
      setProfile((prev) => ({
        ...prev,
        new_password: '',
        confirm_password: '',
      }));
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="text-sm text-green-700">{success}</div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="full_name"
            className="block text-sm font-medium text-gray-700"
          >
            Nom complet
          </label>
          <input
            type="text"
            name="full_name"
            id="full_name"
            value={profile.full_name || ''}
            onChange={handleChange}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            value={profile.email || ''}
            disabled
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm"
          />
        </div>

        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Changer le mot de passe
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Laissez vide pour conserver le mot de passe actuel
          </p>

          <div className="mt-4 space-y-4">
            <div>
              <label
                htmlFor="new_password"
                className="block text-sm font-medium text-gray-700"
              >
                Nouveau mot de passe
              </label>
              <input
                type="password"
                name="new_password"
                id="new_password"
                value={profile.new_password}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="confirm_password"
                className="block text-sm font-medium text-gray-700"
              >
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                name="confirm_password"
                id="confirm_password"
                value={profile.confirm_password}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;
